/**
 * Dialog „Nachweis anlegen“ – auf der Seite "Nachweise" und am Antragsbaustein im Baum.
 *
 * Zwei Schritte: erst die Nachweisart als Kachel (ChoiceTiles), dann das Formular. Dort steht die
 * Nachweisart vorbelegt und änderbar oben; die Auswahl „Nachweis“ zeigt nur die Katalogeinträge
 * dieser Art, die Bezeichnung kommt aus dem Katalog. Nur unter „Sonstige“ (ganz unten) tippt man
 * sie selbst; der freie Nachweis bekommt die gewählte Nachweisart. Ein Zurück zu den Kacheln gibt
 * es nicht – das Feld Nachweisart ersetzt es. Verantwortlich ist vorbelegt mit dem angemeldeten
 * Benutzer. Der Bearbeitungsweg steht auf dem, den die Nachweisart vorbelegt, und folgt einem
 * Wechsel der Art, bis man ihn selbst wählt. Ein neuer Nachweis startet immer im Status „Offen“ –
 * der Dialog fragt deshalb keine Termine ab.
 *
 * Derselbe Dialog auf beiden Seiten: Auf der Seite „Nachweise“ startet die Auswahl der
 * Antragsbausteine leer, am Baum der Seite „Antrag“ mit dem Baustein, an dem man ihn geöffnet hat.
 * Oben in der Auswahl stehen die Bausteine, die die Regel zum gewählten Katalogeintrag empfiehlt.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import DialogTemplate from "../../../layout/DialogTemplate";
import Button from "../../../common/inputs/Button";
import ChoiceTiles from "../../../common/inputs/ChoiceTiles";
import Dropdown from "../../../common/inputs/Dropdown";
import InputField from "../../../common/inputs/InputField";
import MultiSelectDropdown from "../../../common/inputs/MultiSelectDropdown";
import { bausteinOptionen } from "./bausteinOptionen";
import { evidenceTypeTiles } from "./evidenceTypeTiles";
import {
  ApplicationComponentTypeOption,
  EvidenceCatalogOption,
  EvidenceCreate,
  EvidenceTypeOption,
  ProcessingPath,
  ProcessingPathOption,
} from "../../../../types";
import { OrgUser } from "../../../../services/api/users";
import { processingPathTooltip } from "./processingPathTooltip";

/** Wert der Auswahl „Sonstige“ – nie eine Katalog-ID */
const SONSTIGE = "__sonstige__";

interface NachweisAnlegenDialogProps {
  open: boolean;
  nachweisKatalog: EvidenceCatalogOption[];
  /** Nachweisarten – die Kacheln des ersten Schritts und das Feld Nachweisart */
  evidenceTypeOptions: EvidenceTypeOption[];
  /** Bearbeitungswege zur Wahl, je mit Einzeiler als Tooltip */
  processingPathOptions: ProcessingPathOption[];
  orgUsers: OrgUser[];
  /** Baustein-Typen zur Wahl */
  componentTypeOptions: ApplicationComponentTypeOption[];
  /** Vorbelegte Baustein-Typen – am Baum der Baustein, an dem der Dialog geöffnet wurde */
  vorbelegteBausteinTypIds?: string[];
  /** IAM-ID des angemeldeten Benutzers – Vorbelegung von Verantwortlich */
  aktuellerBenutzerId?: string;
  onClose: () => void;
  /** Legt den Nachweis an; `false`, wenn der Server abgelehnt hat (Dialog bleibt offen) */
  onSubmit: (data: EvidenceCreate) => Promise<boolean>;
}

const NachweisAnlegenDialog: React.FC<NachweisAnlegenDialogProps> = ({
  open,
  nachweisKatalog,
  evidenceTypeOptions,
  processingPathOptions,
  orgUsers,
  componentTypeOptions,
  vorbelegteBausteinTypIds,
  aktuellerBenutzerId,
  onClose,
  onSubmit,
}) => {
  const [auswahl, setAuswahl] = useState("");
  const [bezeichnung, setBezeichnung] = useState("");
  const [evidenceTypeId, setEvidenceTypeId] = useState("");
  /** Selbst gewählter Bearbeitungsweg; leer heißt der vorbelegte der Nachweisart */
  const [chosenPath, setChosenPath] = useState<ProcessingPath | "">("");
  const [verantwortlichId, setVerantwortlichId] = useState("");
  const [bausteinTypIds, setBausteinTypIds] = useState<string[]>([]);
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const [laeuft, setLaeuft] = useState(false);

  // Beim Öffnen schon im Rendern auf die Kacheln zurück, nicht erst im Effekt: sonst stünde für
  // einen Frame das Formular des vorigen Anlaufs da, in anderer Breite.
  const [warOffen, setWarOffen] = useState(open);
  if (open !== warOffen) {
    setWarOffen(open);
    if (open) setEvidenceTypeId("");
  }

  useEffect(() => {
    if (!open) return;
    setAuswahl("");
    setBezeichnung("");
    setChosenPath("");
    setBausteinTypIds(vorbelegteBausteinTypIds ?? []);
    setVerantwortlichId(
      orgUsers.some((u) => u.user_id === aktuellerBenutzerId)
        ? (aktuellerBenutzerId ?? "")
        : "",
    );
    setFehler({});
    // Nur beim Öffnen vorbelegen; ein neues Array desselben Inhalts setzt die Auswahl nicht zurück.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orgUsers, aktuellerBenutzerId]);

  const tiles = useMemo(
    () => evidenceTypeTiles(evidenceTypeOptions, nachweisKatalog),
    [evidenceTypeOptions, nachweisKatalog],
  );
  const nachweisOptionen = useMemo(
    () => [
      ...nachweisKatalog
        .filter((eintrag) => eintrag.evidence_type.id === evidenceTypeId)
        .map((eintrag) => ({
          value: eintrag.id,
          label: eintrag.bezeichnung,
        })),
      { value: SONSTIGE, label: "Sonstige", dividerBefore: true },
    ],
    [nachweisKatalog, evidenceTypeId],
  );
  const artOptionen = useMemo(
    () => evidenceTypeOptions.map((t) => ({ value: t.id, label: t.name })),
    [evidenceTypeOptions],
  );
  const pfadOptionen = useMemo(
    () =>
      processingPathOptions.map((o) => ({
        value: o.value,
        label: o.label,
        tooltip: processingPathTooltip(o),
      })),
    [processingPathOptions],
  );
  const path =
    chosenPath ||
    evidenceTypeOptions.find((t) => t.id === evidenceTypeId)
      ?.default_processing_path ||
    "";
  const currentPath = processingPathOptions.find((o) => o.value === path);
  const benutzerOptionen = useMemo(
    () => orgUsers.map((u) => ({ value: u.user_id, label: u.name || u.email })),
    [orgUsers],
  );

  const sonstige = auswahl === SONSTIGE;
  const katalogeintrag = nachweisKatalog.find((e) => e.id === auswahl);
  const bausteinAuswahl = useMemo(
    () => bausteinOptionen(componentTypeOptions, katalogeintrag),
    [componentTypeOptions, katalogeintrag],
  );

  /** Ein Katalogeintrag einer anderen Art fällt weg; „Sonstige“ samt Bezeichnung bleibt. */
  const changeEvidenceType = (typeId: string) => {
    setEvidenceTypeId(typeId);
    if (katalogeintrag && katalogeintrag.evidence_type.id !== typeId) {
      setAuswahl("");
    }
  };

  const pruefen = (): boolean => {
    const gefunden: Record<string, string> = {};
    if (!auswahl) gefunden.auswahl = "Pflichtfeld";
    if (sonstige && !bezeichnung.trim()) gefunden.bezeichnung = "Pflichtfeld";
    setFehler(gefunden);
    return Object.keys(gefunden).length === 0;
  };

  const handleSubmit = async () => {
    if (!pruefen()) return;
    const eintrag = katalogeintrag;
    setLaeuft(true);
    try {
      const ok = await onSubmit({
        evidence_type_id: evidenceTypeId,
        processing_path: path || undefined,
        bezeichnung: sonstige ? bezeichnung.trim() : eintrag?.bezeichnung,
        verantwortlich_id: verantwortlichId || undefined,
        evidence_catalog_entry_id: eintrag?.id,
        application_component_type_ids: bausteinTypIds,
      });
      if (ok) onClose();
    } finally {
      setLaeuft(false);
    }
  };

  if (!evidenceTypeId) {
    return (
      <DialogTemplate
        open={open}
        onClose={onClose}
        title="Nachweis anlegen"
        maxWidth="md"
      >
        <ChoiceTiles tiles={tiles} onSelect={setEvidenceTypeId} />
      </DialogTemplate>
    );
  }

  return (
    <DialogTemplate
      open={open}
      onClose={onClose}
      title="Nachweis anlegen"
      maxWidth="sm"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={laeuft}>
            Nachweis anlegen
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
        <Dropdown
          label="Nachweisart"
          value={evidenceTypeId}
          options={artOptionen}
          onChange={changeEvidenceType}
          variant="floating"
          size="small"
          required
        />

        <Dropdown
          label="Nachweis"
          value={auswahl}
          options={nachweisOptionen}
          onChange={(wert) => {
            setAuswahl(wert);
            setFehler((prev) => ({ ...prev, auswahl: "" }));
          }}
          variant="floating"
          size="small"
          required
          error={!!fehler.auswahl}
          helperText={fehler.auswahl}
        />

        {sonstige && (
          <InputField
            label="Bezeichnung"
            value={bezeichnung}
            onChange={setBezeichnung}
            required
            error={fehler.bezeichnung}
            size="small"
            autoFocus
          />
        )}

        <Dropdown
          label="Bearbeitungsweg"
          value={path}
          options={pfadOptionen}
          onChange={(wert) => setChosenPath(wert as ProcessingPath)}
          variant="floating"
          size="small"
          tooltip={currentPath && processingPathTooltip(currentPath)}
        />

        <Dropdown
          label="Verantwortlich"
          value={verantwortlichId}
          options={benutzerOptionen}
          onChange={setVerantwortlichId}
          variant="floating"
          size="small"
          searchable
        />

        <MultiSelectDropdown
          label="Antragsbausteine"
          values={bausteinTypIds}
          options={bausteinAuswahl}
          onChange={setBausteinTypIds}
          size="small"
          searchable
          zeilenweise
        />
      </Box>
    </DialogTemplate>
  );
};

export default NachweisAnlegenDialog;
